/**
 * Phase 131D — Academy Top Moments (public brand page)
 * ----------------------------------------------------
 * Route: /livex/academy/:slug/top-moments  (PUBLIC, no auth)
 *
 * Strategic role: turns highlight metadata into discoverable academy marketing
 * assets. Each academy gets an auto-updating digital trophy wall — SEO-indexable,
 * shareable, emotionally rich. Zero new infra. Zero AI. Just smart aggregation
 * of livex_highlights.
 *
 * Doctrine: emotional + cinematic (NOT dashboard-like). Phase 131A readability
 * tokens preserved (white text, thick borders, no opacity tricks).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '@/styles/livex.css';
import {
  Trophy, ArrowLeft, ExternalLink, Calendar, Eye, Share2,
  Loader2, AlertTriangle, Radio, Copy, CheckCircle2, MapPin,
  Play, X, Clock,
} from 'lucide-react';
import LivexFeaturedBadge, { LivexFeaturedFooterStrip } from '@/components/livex/LivexFeaturedBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------- helpers ------------------------------------------------- */

function useCountUp(target, durationMs = 1400) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const t0 = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return val;
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return ''; }
}

/* ---------- atoms --------------------------------------------------- */

function StatCounter({ value, label, emoji, accent }) {
  const animated = useCountUp(value);
  return (
    <div
      className="p-5 bg-[#07111F] border-2 border-[#22314F] rounded-xl text-center"
      style={{ borderTopColor: accent, borderTopWidth: '4px' }}
      data-testid={`livex-academy-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="text-4xl mb-1 leading-none" aria-hidden>{emoji}</div>
      <div className="text-3xl sm:text-4xl font-black text-white tabular-nums leading-none mb-2">
        {animated.toLocaleString()}
      </div>
      <div className="text-[12px] sm:text-[13px] font-black uppercase tracking-[0.1em] text-white">
        {label}
      </div>
    </div>
  );
}

function HeroStat({ value, label, Icon, testid }) {
  const animated = useCountUp(value);
  return (
    <div className="p-4 sm:p-5 bg-black/60 border-2 border-[#22314F] rounded-xl backdrop-blur">
      <Icon className="h-5 w-5 text-[#FF5A1F] mb-1.5" />
      <div className="text-3xl sm:text-4xl font-black text-white tabular-nums leading-none" data-testid={testid}>
        {animated.toLocaleString()}
      </div>
      <div className="livex-label mt-2" style={{ marginBottom: 0 }}>{label}</div>
    </div>
  );
}

function fmtClipDuration(sec) {
  const s = Math.max(0, Math.round(Number(sec || 0)));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${String(s % 60).padStart(2, '0')}s`;
}

function MomentCard({ m, onPlayClip }) {
  // Phase 140B.2 Sprint 4 #3 — if the moment has a ready extracted
  // clip, render a rich MP4 card with hover-to-preview + Play CTA that
  // opens the modal player. Moments without a clip keep the legacy
  // emoji thumbnail (zero regression for un-extracted history).
  const clip = m.clip;
  const cardRef = useRef(null);
  const videoRef = useRef(null);

  const startHoverPreview = () => {
    if (!clip || !videoRef.current) return;
    // Muted autoplay loop on hover; pause + reset on exit so we don't
    // burn bandwidth on offscreen cards in a long scroll.
    try { videoRef.current.currentTime = 0; videoRef.current.play().catch(() => {}); }
    catch { /* noop */ }
  };
  const stopHoverPreview = () => {
    if (!videoRef.current) return;
    try { videoRef.current.pause(); videoRef.current.currentTime = 0; } catch { /* noop */ }
  };

  // When a clip exists, the whole card becomes a Play trigger (opens
  // the lightbox); the Watch-full-match link still lives in the body
  // footer as a secondary CTA so we don't lose SEO context.
  const ThumbWrap = clip ? 'button' : Link;
  const wrapProps = clip
    ? { type: 'button', onClick: () => onPlayClip(m), 'data-testid': `livex-academy-moment-${m.id}` }
    : { to: m.stream?.watch_path || '#', 'data-testid': `livex-academy-moment-${m.id}` };

  return (
    <article
      ref={cardRef}
      onMouseEnter={startHoverPreview}
      onMouseLeave={stopHoverPreview}
      className="group block bg-[#07111F] border-2 border-[#22314F] rounded-xl overflow-hidden hover:border-[#FF5A1F] transition-all hover:shadow-2xl hover:shadow-[#FF5A1F]/10"
    >
      <ThumbWrap
        {...wrapProps}
        className="aspect-video relative overflow-hidden block w-full text-left"
        style={!clip ? {
          background: `radial-gradient(circle at center, ${m.accent}22, #0a0a0f 70%)`,
        } : { background: '#050a14' }}
      >
        {clip ? (
          <>
            {/* Real MP4 preview — poster + muted autoplay on hover. */}
            <video
              ref={videoRef}
              src={clip.mp4_url}
              poster={clip.thumbnail_url || undefined}
              muted
              loop
              playsInline
              preload="metadata"
              className="absolute inset-0 w-full h-full object-cover"
              data-testid={`livex-academy-moment-${m.id}-video`}
            />
            {/* Play overlay — pulses subtly to attract the eye on first paint. */}
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <div
                className="h-14 w-14 rounded-full grid place-items-center transition-transform duration-500 group-hover:scale-110 group-hover:opacity-90 opacity-95"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,90,31,0.92), rgba(220,38,38,0.92))',
                  boxShadow: '0 14px 36px rgba(255,90,31,0.40)',
                }}
              >
                <Play className="h-5 w-5 text-white ml-0.5" fill="currentColor" />
              </div>
            </div>
            {/* Duration pill (bottom-right). */}
            <div
              className="absolute bottom-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold tabular-nums bg-black/70 text-white"
              style={{ border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(10px)' }}
              data-testid={`livex-academy-moment-${m.id}-duration`}
            >
              <Clock className="h-2.5 w-2.5" /> {fmtClipDuration(clip.duration_seconds)}
            </div>
          </>
        ) : (
          <>
            {/* Legacy emoji "thumbnail" — fallback for moments without an
                extracted clip yet. Identical to pre-Sprint-4 behaviour. */}
            <div className="absolute inset-0 opacity-30" style={{
              background: 'repeating-linear-gradient(45deg, transparent, transparent 12px, rgba(255,255,255,0.02) 12px, rgba(255,255,255,0.02) 14px)',
            }} />
            <div className="absolute inset-0 grid place-items-center">
              <div className="text-[6rem] sm:text-[7rem] leading-none transition-transform group-hover:scale-110" aria-hidden>
                {m.emoji}
              </div>
            </div>
            <div className="absolute bottom-3 right-3 px-2.5 py-1.5 bg-[#FF5A1F] rounded-md text-[11px] font-black uppercase tracking-[0.08em] text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
              <ExternalLink className="h-3 w-3" /> Watch
            </div>
          </>
        )}
        {/* Match-time chip (always rendered). */}
        <div className="absolute top-3 left-3 px-2.5 py-1 bg-black/85 border-2 border-[#22314F] rounded-md font-mono text-[12px] font-bold text-white tabular-nums">
          {m.match_time_label}
        </div>
        {/* Type badge (always rendered). */}
        <div
          className="absolute top-3 right-3 px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-[0.1em] text-white"
          style={{ background: m.accent, border: `2px solid ${m.accent}` }}
        >
          {m.label}
        </div>
      </ThumbWrap>
      <div className="p-4">
        <h3 className="text-base font-black text-white truncate">{m.stream?.title || 'Untitled match'}</h3>
        <div className="flex items-center gap-2 mt-1.5 text-[12px] font-bold text-[#94A3B8]">
          {m.stream?.sport && <span className="capitalize">{m.stream.sport}</span>}
          {m.created_at && <><span>·</span><span>{formatDate(m.created_at)}</span></>}
        </div>
        {m.note && (
          <p className="livex-body-text mt-2 line-clamp-2" style={{ fontSize: '13px' }}>
            {m.note}
          </p>
        )}
        {clip && m.stream?.watch_path && (
          <Link
            to={m.stream.watch_path}
            className="inline-flex items-center gap-1 mt-3 text-[11px] font-bold text-[#94A3B8] hover:text-[#FF5A1F] transition-colors"
            data-testid={`livex-academy-moment-${m.id}-watch-full`}
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-3 w-3" /> Watch full match
          </Link>
        )}
      </div>
    </article>
  );
}

function MomentLightbox({ moment, onClose }) {
  if (!moment || !moment.clip) return null;
  const clip = moment.clip;
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center px-4 py-6 bg-black/85"
      onClick={onClose}
      data-testid="livex-academy-moment-lightbox"
    >
      <div
        className="relative w-full max-w-4xl rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.10)', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close clip player"
          data-testid="livex-academy-moment-lightbox-close"
          className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full grid place-items-center bg-black/60 border border-white/20 text-white hover:bg-black"
        >
          <X className="h-4 w-4" />
        </button>
        <video
          src={clip.mp4_url}
          poster={clip.thumbnail_url || ''}
          controls
          autoPlay
          playsInline
          className="w-full aspect-video bg-black"
          data-testid="livex-academy-moment-lightbox-video"
        />
        <div className="px-5 py-4 border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          <h3 className="text-base font-bold text-white truncate">{clip.title || moment.stream?.title || 'Match Moment'}</h3>
          <div className="mt-1 text-[12px] font-medium text-white/55">
            {moment.label}
            {moment.match_time_label && moment.match_time_label !== '—' && (
              <> · {moment.match_time_label}</>
            )}
            {moment.created_at && <> · {formatDate(moment.created_at)}</>}
          </div>
        </div>
      </div>
    </div>
  );
}

function ShareButtons({ academy, total }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const text = `${academy.name} — ${total} highlight moments captured live on StreamX.`;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };
  const tweet = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
  const wa = `https://wa.me/?text=${encodeURIComponent(text + ' ' + shareUrl)}`;
  const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="livex-academy-share">
      <a href={tweet} target="_blank" rel="noopener noreferrer" data-testid="livex-academy-share-twitter"
        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white bg-[#07111F] border-2 border-[#22314F] hover:border-[#FF5A1F] rounded-lg transition-all">
        𝕏 Tweet
      </a>
      <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="livex-academy-share-whatsapp"
        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white bg-[#07111F] border-2 border-[#22314F] hover:border-[#FF5A1F] rounded-lg transition-all">
        WhatsApp
      </a>
      <a href={fb} target="_blank" rel="noopener noreferrer" data-testid="livex-academy-share-facebook"
        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white bg-[#07111F] border-2 border-[#22314F] hover:border-[#FF5A1F] rounded-lg transition-all">
        Facebook
      </a>
      <button onClick={onCopy} data-testid="livex-academy-share-copy"
        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white bg-[#07111F] border-2 border-[#22314F] hover:border-[#FF5A1F] rounded-lg transition-all">
        {copied ? <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy Link</>}
      </button>
    </div>
  );
}

/* ---------- page --------------------------------------------------- */

export default function LivexAcademyTopMoments() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [season, setSeason] = useState('current');
  // Phase 140B.2 Sprint 4 #3 — modal player for the new MP4 thumbnails.
  // Null = closed. Setting it to a moment object opens the lightbox.
  const [activeClipMoment, setActiveClipMoment] = useState(null);

  useEffect(() => {
    if (!slug) return undefined;
    let active = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/livex/academy/${slug}/top-moments?season=${season}`);
        if (!active) return;
        setData(r.data);
        setError('');
      } catch (e) {
        if (!active) return;
        setError(e?.response?.data?.detail || 'Academy not found');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [slug, season]);

  // SEO metadata injection.
  useEffect(() => {
    if (!data?.academy) return;
    const a = data.academy;
    const topSport = a.sports?.[0]
      ? a.sports[0].charAt(0).toUpperCase() + a.sports[0].slice(1)
      : 'Sports';
    const title = `${a.name} — Top ${topSport} Moments | StreamX`;
    const desc = `Watch the best goals, saves, and match highlights from ${a.name}${a.city ? `, ${a.city}` : ''}. ${data.stats.total_highlights} highlight moments across ${data.stats.total_streams} matches on StreamX.`;
    document.title = title;
    const setMeta = (selector, attr, value, attrName, attrValue) => {
      let m = document.querySelector(selector);
      if (!m) {
        m = document.createElement('meta');
        m.setAttribute(attrName, attrValue);
        document.head.appendChild(m);
      }
      m.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]', 'content', desc, 'name', 'description');
    setMeta('meta[property="og:title"]', 'content', title, 'property', 'og:title');
    setMeta('meta[property="og:description"]', 'content', desc, 'property', 'og:description');
    setMeta('meta[property="og:type"]', 'content', 'website', 'property', 'og:type');
    setMeta('meta[property="og:url"]', 'content', typeof window !== 'undefined' ? window.location.href : '', 'property', 'og:url');
    if (a.logo_url) {
      setMeta('meta[property="og:image"]', 'content', a.logo_url, 'property', 'og:image');
    }
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image', 'name', 'twitter:card');
    setMeta('meta[name="twitter:title"]', 'content', title, 'name', 'twitter:title');
    setMeta('meta[name="twitter:description"]', 'content', desc, 'name', 'twitter:description');
  }, [data]);

  const filteredMoments = useMemo(() => {
    if (!data?.moments) return [];
    if (filterType === 'all') return data.moments;
    return data.moments.filter((m) => m.type === filterType);
  }, [data, filterType]);

  /* ---------- LOADING ---------- */
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-white" style={{ background: '#0a0a0f' }} data-testid="livex-academy-loading">
        <div className="flex items-center gap-3 text-base font-bold">
          <Loader2 className="h-5 w-5 animate-spin text-[#FF5A1F]" /> Loading academy highlights…
        </div>
      </div>
    );
  }

  /* ---------- ERROR ---------- */
  if (error || !data) {
    return (
      <div className="min-h-screen grid place-items-center text-white px-6" style={{ background: '#0a0a0f' }} data-testid="livex-academy-error">
        <div className="max-w-md w-full text-center">
          <div className="h-16 w-16 rounded-2xl mx-auto mb-4 grid place-items-center bg-[#1a0e10] border-2 border-rose-700">
            <AlertTriangle className="h-7 w-7 text-[#FF5A1F]" />
          </div>
          <h1 className="text-2xl font-black text-white mb-2">Academy not found</h1>
          <p className="livex-body-text mb-6">{error || 'Check the URL and try again.'}</p>
          <button onClick={() => navigate('/streamx')} data-testid="livex-academy-error-back"
            className="inline-flex items-center gap-2 px-5 py-3 text-base font-bold text-white border-2 border-[#FF5A1F] rounded-lg"
            style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)', fontSize: '16px' }}>
            <ArrowLeft className="h-4 w-4" /> Back to StreamX
          </button>
        </div>
      </div>
    );
  }

  const { academy, stats, moments } = data;
  const featured_status = data.featured_status || null;
  const allTypeOption = { type: 'all', label: 'All', emoji: '✨', count: stats.total_highlights };

  /* ---------- MAIN ---------- */
  return (
    <div className="min-h-screen text-white" style={{ background: '#0a0a0f' }} data-testid="livex-academy-top-moments">

      {/* ===== HERO ===== */}
      <header className="relative overflow-hidden border-b-2 border-[#22314F]">
        {/* Gradient curtain — cinematic, not dashboard-y */}
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(80% 60% at 10% 0%, rgba(255,90,31,0.18), transparent 60%), radial-gradient(60% 50% at 90% 100%, rgba(220,38,38,0.22), transparent 60%), #07111F',
        }} />
        {/* Subtle grain */}
        <div className="absolute inset-0 opacity-25" style={{
          background: 'repeating-linear-gradient(45deg, transparent, transparent 14px, rgba(255,255,255,0.02) 14px, rgba(255,255,255,0.02) 16px)',
        }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
          {/* Top nav strip */}
          <div className="flex items-center justify-between gap-3 mb-8 flex-wrap">
            <button onClick={() => navigate('/streamx')} data-testid="livex-academy-back" className="inline-flex items-center gap-2 text-sm font-bold text-white hover:text-[#FF5A1F] transition-colors">
              <ArrowLeft className="h-4 w-4" /> Back to StreamX
            </button>
            <div className="text-xs tracking-[0.3em] font-black text-[#FF5A1F]">STREAMX · TOP MOMENTS</div>
          </div>

          {/* Hero content */}
          <div className="flex items-start gap-4 sm:gap-6 flex-wrap">
            {academy.logo_url && (
              <img
                src={academy.logo_url}
                alt={`${academy.name} logo`}
                className="h-20 w-20 sm:h-28 sm:w-28 rounded-2xl border-4 border-[#22314F] bg-[#0a0a0f] object-contain flex-shrink-0"
                data-testid="livex-academy-logo"
              />
            )}
            <div className="flex-1 min-w-0">
              <h1
                className="text-3xl sm:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight"
                data-testid="livex-academy-name"
              >
                {academy.name}
              </h1>
              {academy.tagline && (
                <p className="livex-body-text mt-2 sm:mt-3" style={{ fontSize: '16px' }}>{academy.tagline}</p>
              )}
              {/* Phase 135E — Featured This Week prestige badge */}
              {featured_status && (
                <div className="mt-3 sm:mt-4">
                  <LivexFeaturedBadge featured={featured_status} />
                </div>
              )}
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                {(academy.city || academy.state) && (
                  <span className="inline-flex items-center gap-1.5 text-sm font-bold text-white">
                    <MapPin className="h-4 w-4 text-[#FF5A1F]" />
                    {[academy.city, academy.state].filter(Boolean).join(', ')}
                  </span>
                )}
                {academy.sports?.map((sp) => (
                  <span key={sp} className="px-2.5 py-1 rounded-md bg-[#07111F] border-2 border-[#22314F] text-xs font-black uppercase tracking-[0.08em] text-white capitalize">
                    {sp}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Headline stat strip */}
          <div className="mt-8 sm:mt-10 grid grid-cols-3 gap-3 sm:gap-4">
            <HeroStat value={stats.total_highlights} label="Highlights" Icon={Trophy} testid="livex-academy-stat-total-highlights" />
            <HeroStat value={stats.total_streams} label="Live Matches" Icon={Radio} testid="livex-academy-stat-total-streams" />
            <HeroStat value={stats.total_peak_viewers} label="Peak Audience" Icon={Eye} testid="livex-academy-stat-peak-viewers" />
          </div>

          {/* Share strip */}
          <div className="mt-6 sm:mt-8 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-sm font-bold text-white">
              <Share2 className="h-4 w-4 text-[#FF5A1F]" /> Share this academy
            </div>
            <ShareButtons academy={academy} total={stats.total_highlights} />
          </div>
        </div>
      </header>

      {/* ===== BY-TYPE COUNTER STRIP ===== */}
      {stats.by_type.length > 0 && (
        <section className="border-b-2 border-[#22314F] bg-[#050a14]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {stats.by_type.map((t) => (
                <StatCounter
                  key={t.type}
                  value={t.count}
                  label={t.label}
                  emoji={t.emoji}
                  accent={t.accent}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== FILTER + SEASON TOGGLE ===== */}
      <section className="border-b-2 border-[#22314F] bg-[#07111F]/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-black uppercase tracking-[0.1em] text-[#94A3B8] mr-1">Filter</span>
            <button
              onClick={() => setFilterType('all')}
              data-testid="livex-academy-filter-all"
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-[0.08em] rounded-md border-2 transition-all ${
                filterType === 'all'
                  ? 'bg-[#FF5A1F] border-[#FF5A1F] text-white'
                  : 'bg-[#07111F] border-[#22314F] text-white hover:border-[#FF5A1F]'
              }`}
            >
              ✨ All · {allTypeOption.count}
            </button>
            {stats.by_type.map((t) => (
              <button
                key={t.type}
                onClick={() => setFilterType(t.type)}
                data-testid={`livex-academy-filter-${t.type}`}
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-[0.08em] rounded-md border-2 transition-all ${
                  filterType === t.type
                    ? 'bg-[#FF5A1F] border-[#FF5A1F] text-white'
                    : 'bg-[#07111F] border-[#22314F] text-white hover:border-[#FF5A1F]'
                }`}
              >
                {t.emoji} {t.label} · {t.count}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-black uppercase tracking-[0.1em] text-[#94A3B8]">Window</span>
            <button
              onClick={() => setSeason('current')}
              data-testid="livex-academy-season-current"
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-[0.08em] rounded-md border-2 transition-all ${
                season === 'current' ? 'bg-[#FF5A1F] border-[#FF5A1F] text-white' : 'bg-[#07111F] border-[#22314F] text-white hover:border-[#FF5A1F]'
              }`}
            >
              <Calendar className="inline h-3 w-3 mr-1" /> Season
            </button>
            <button
              onClick={() => setSeason('all')}
              data-testid="livex-academy-season-all"
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-[0.08em] rounded-md border-2 transition-all ${
                season === 'all' ? 'bg-[#FF5A1F] border-[#FF5A1F] text-white' : 'bg-[#07111F] border-[#22314F] text-white hover:border-[#FF5A1F]'
              }`}
            >
              All Time
            </button>
          </div>
        </div>
      </section>

      {/* ===== MOMENTS GRID ===== */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {moments.length === 0 ? (
          <div className="py-20 text-center" data-testid="livex-academy-empty">
            <Trophy className="h-12 w-12 mx-auto text-[#94A3B8] mb-3" />
            <h2 className="text-2xl font-black text-white mb-2">No moments yet</h2>
            <p className="livex-body-text mb-6 max-w-md mx-auto">
              When {academy.name} broadcasts their next match, every goal, save, and celebration will appear here automatically.
            </p>
            <Link to="/streamx" data-testid="livex-academy-empty-cta"
              className="inline-flex items-center gap-2 px-5 py-3 text-base font-bold text-white border-2 border-[#FF5A1F] rounded-lg"
              style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
              <Radio className="h-4 w-4" /> Explore StreamX
            </Link>
          </div>
        ) : filteredMoments.length === 0 ? (
          <div className="py-12 text-center" data-testid="livex-academy-filter-empty">
            <p className="livex-body-text">No {filterType.replace('_', ' ')} moments in this window. Try another filter.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="livex-academy-grid">
            {filteredMoments.map((m) => (
              <MomentCard key={m.id} m={m} onPlayClip={setActiveClipMoment} />
            ))}
          </div>
        )}
      </main>

      {/* Phase 135E — Recognized by StreamX footer strip (only if featured) */}
      {featured_status && <LivexFeaturedFooterStrip featured={featured_status} />}

      {/* ===== FOOTER CTA ===== */}
      <footer className="border-t-2 border-[#22314F] bg-[#050a14]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10 text-center">
          <div className="text-xs tracking-[0.3em] font-black text-[#FF5A1F] mb-2">POWERED BY STREAMX</div>
          <h3 className="text-2xl sm:text-3xl font-black text-white mb-2">Broadcast your academy like a pro.</h3>
          <p className="livex-body-text mb-5 max-w-xl mx-auto">
            Every academy on StreamX gets a free auto-updating brand page like this one. Live streams, instant highlights, parent-shareable moments — zero technical setup.
          </p>
          <Link to="/streamx" data-testid="livex-academy-footer-cta"
            className="inline-flex items-center gap-2 px-5 py-3 text-base font-bold text-white border-2 border-[#FF5A1F] rounded-lg"
            style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
            <Radio className="h-4 w-4" /> Start a free academy
          </Link>
        </div>
      </footer>

      {/* Phase 140B.2 Sprint 4 #3 — modal player for MP4-enriched moments. */}
      <MomentLightbox moment={activeClipMoment} onClose={() => setActiveClipMoment(null)} />
    </div>
  );
}
